import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "17 July 2026";
const CONTACT_EMAIL = "privacy@philsnw.co";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="privacy-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] mb-4"
        data-testid="privacy-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <span className="h-10 w-10 rounded-2xl bg-[hsl(var(--primary))] text-white inline-flex items-center justify-center">
          <Shield className="h-5 w-5" />
        </span>
        <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">Legal</div>
      </div>
      <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight">
        Privacy Policy
      </h1>
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
        Effective date: {EFFECTIVE_DATE}
      </p>

      <div className="mt-8 bg-white border border-[hsl(var(--border))] rounded-3xl p-6 sm:p-8 space-y-6 text-[15px] leading-relaxed">
        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">1. Who we are</h2>
          <p>
            SocialHub (“we”, “us”, “the app”) is a social meeting platform that helps
            people nearby with shared interests connect and plan meetups. This policy
            explains what we collect, why, and the choices you have. It applies to the
            web app at <strong>philsnw.co</strong> and the SocialHub Android app
            (package <code>com.socialhub.myapp</code>), which share the same backend.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">2. Data we collect</h2>
          <ul className="list-disc ps-5 space-y-1.5">
            <li><strong>Account:</strong> alias (display name), password hash, verification status, role.</li>
            <li><strong>Profile:</strong> city, languages spoken, primary and secondary activity preferences, preferred group size, optional avatar image.</li>
            <li><strong>Location:</strong> latitude and longitude captured at registration (with your consent) to compute distance for the 5&nbsp;km Discover match radius. Stored once; not tracked continuously.</li>
            <li><strong>Social graph:</strong> connection requests, group memberships, invites.</li>
            <li><strong>Communication:</strong> direct messages, group chat messages, RSVPs, typing/last-seen timestamps.</li>
            <li><strong>Activity:</strong> AI-generated event suggestions you receive, ideas you save (♥ shelf), scheduled events, notifications.</li>
            <li><strong>Push:</strong> device push tokens (Expo Push token, platform label) if you opt in on the Android app.</li>
            <li><strong>Diagnostic:</strong> minimal server logs (timestamp, endpoint, status). No IP addresses are retained beyond routine log rotation.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">3. How we use it</h2>
          <ul className="list-disc ps-5 space-y-1.5">
            <li>Authenticate you and keep your session (JWT).</li>
            <li>Match you with nearby users within a 5&nbsp;km radius on Discover.</li>
            <li>Deliver 1-on-1 and group messages, invites, RSVPs, and notifications.</li>
            <li>Generate personalised event suggestions and cover art (see §4).</li>
            <li>Show your last-seen and typing status to accepted connections.</li>
            <li>Deliver push notifications, if you opted in.</li>
            <li>Enforce safety rules and moderate abuse (admin verification/removal).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">4. AI and third-party services</h2>
          <p>
            To generate event ideas and cover art, we send a compact profile summary
            (activities, group size, city, preferences — <em>never</em> your password, chat
            content, or precise coordinates) to:
          </p>
          <ul className="list-disc ps-5 space-y-1.5 mt-2">
            <li><strong>Anthropic Claude Sonnet</strong> — text ideation.</li>
            <li><strong>Google Gemini (Nano Banana)</strong> — cover-image generation.</li>
          </ul>
          <p className="mt-2">
            Requests are routed through the Emergent Universal LLM key. Outputs are
            stored in your account and shown only to you (and, for group events, to
            group members). We do not sell or license your data to advertisers.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">5. Sharing</h2>
          <p>We do not sell your data. Data is shared only:</p>
          <ul className="list-disc ps-5 space-y-1.5 mt-2">
            <li>With other users you deliberately interact with (Discover match cards, connection acceptances, group members).</li>
            <li>With third-party processors listed in §4 for the sole purpose of generating your requested content.</li>
            <li>With Google Play services (push token delivery) when you opt in on Android.</li>
            <li>When required by law.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">6. Retention &amp; deletion</h2>
          <p>
            Your data lives in our database while your account is active. You can
            delete your account at any time from Profile → Delete Account, which
            removes your profile, chats, connections, saved ideas, and push tokens.
            Backend backups roll off within 30 days. To request an export or manual
            deletion, email <a className="text-[hsl(var(--primary))] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">7. Your rights</h2>
          <p>
            Depending on your region (EU/UK GDPR, California CCPA, UAE PDPL, etc.) you
            have the right to access, correct, port, restrict, or delete your data,
            and to withdraw consent for location or push at any time from the app
            settings. We honour requests within 30 days.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">8. Children</h2>
          <p>
            SocialHub is not intended for anyone under 16. We do not knowingly collect
            data from children. If you believe a minor has an account, please email us
            and we will remove it.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">9. Security</h2>
          <p>
            Passwords are stored hashed (bcrypt). Traffic is served over HTTPS. Sensitive
            client secrets on Android are stored in <code>expo-secure-store</code>.
            No system is perfectly secure — please use a unique password and let us
            know if you spot anything unusual.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">10. Changes</h2>
          <p>
            We may update this policy as the product evolves. Material changes will be
            surfaced inside the app before they take effect.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold mb-2">11. Contact</h2>
          <p>
            Questions or requests? Reach us at{" "}
            <a className="text-[hsl(var(--primary))] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </div>
    </div>
  );
}
